import React from "react";
import styled from "styled-components";
import {IconType} from "react-icons";
import {
    GiBelt,
    GiBattleAxe,
    GiBoilingBubbles,
    GiBrain,
    GiChainsaw,
    GiCircuitry,
    GiCoalPile,
    GiCrumblingBall,
    GiFurnace,
    GiGoldMine,
    GiMaterialsScience,
    GiMetalBar,
    GiMetalDisc,
    GiMineWagon,
    GiMiner,
    GiNails,
    GiOilPump,
    GiOre,
    GiPipes,
    GiPowerGenerator,
    GiProcessor,
    GiRobotGrab,
    GiSteelClaws,
    GiStoneBlock,
    GiStonePile,
    GiTestTubes,
    GiWireCoil,
    GiWoodBeam,
    GiGears,
} from "react-icons/gi";
import {FaBoxOpen} from "react-icons/fa";

export type ItemIconConfig = {
    icon: IconType;
    color: string;
    background: string;
};

const ITEM_ICON_MAP: Partial<Record<string, ItemIconConfig>> = {
    coal: {icon: GiCoalPile, color: "#2b2d42", background: "#f1f5f9"},
    wood: {icon: GiWoodBeam, color: "#7c4a03", background: "#fff8e1"},
    iron: {icon: GiOre, color: "#4b5563", background: "#e5e7eb"},
    copper: {icon: GiOre, color: "#b45309", background: "#fff7ed"},
    stone: {icon: GiStonePile, color: "#374151", background: "#f3f4f6"},
    ironplate: {icon: GiMetalBar, color: "#334155", background: "#e2e8f0"},
    copperplate: {icon: GiMetalDisc, color: "#b45309", background: "#fff3e0"},
    copperWire: {icon: GiWireCoil, color: "#c2410c", background: "#fff7ed"},
    ironStick: {icon: GiNails, color: "#475569", background: "#e2e8f0"},
    steelPlate: {icon: GiSteelClaws, color: "#1f2937", background: "#e5e7eb"},
    stoneBrick: {icon: GiStoneBlock, color: "#4b5563", background: "#e5e7eb"},
    electronicCircuit: {icon: GiCircuitry, color: "#047857", background: "#ecfdf3"},
    transportBelt: {icon: GiBelt, color: "#92400e", background: "#fff7ed"},
    inserter: {icon: GiRobotGrab, color: "#0ea5e9", background: "#ecfeff"},
    miner1: {icon: GiGoldMine, color: "#92400e", background: "#fef3c7"},
    miner2: {icon: GiMiner, color: "#0f172a", background: "#e0f2fe"},
    smelter1: {icon: GiFurnace, color: "#c1121f", background: "#fef2f2"},
    circuitAssembler: {icon: GiProcessor, color: "#0f766e", background: "#ecfeff"},
    scienceAssembler: {icon: GiMaterialsScience, color: "#2563eb", background: "#e0f2fe"},
    researchLab: {icon: GiBrain, color: "#7c3aed", background: "#f5f3ff"},
    ironGear: {icon: GiGears, color: "#4b5563", background: "#e5e7eb"},
    ironPipe: {icon: GiPipes, color: "#0f172a", background: "#e2e8f0"},
    waterPump: {icon: GiOilPump, color: "#0284c7", background: "#e0f2fe"},
    waterBoiler: {icon: GiBoilingBubbles, color: "#be123c", background: "#fef2f2"},
    steamGenerator: {icon: GiPowerGenerator, color: "#2563eb", background: "#e0f2fe"},
    sciencePack1: {icon: GiTestTubes, color: "#7c3aed", background: "#f3e8ff"},
    sciencePack2: {icon: GiMaterialsScience, color: "#075985", background: "#e0f2fe"},
    steelFurnace: {icon: GiFurnace, color: "#b45309", background: "#fff4e6"},
    "tech-wood-clearing": {icon: GiCrumblingBall, color: "#8b5a2b", background: "#fff7ed"},
    "tech-steel-axes": {icon: GiBattleAxe, color: "#1f2937", background: "#e2e8f0"},
    "tech-chainsaw": {icon: GiChainsaw, color: "#b45309", background: "#fff4e6"},
    "tech-boulder-clearing": {icon: GiStonePile, color: "#334155", background: "#e2e8f0"},
    "tech-big-drills": {icon: GiMineWagon, color: "#0f172a", background: "#e2e8f0"},
    "tech-steel-smelting": {icon: GiMetalBar, color: "#1f2937", background: "#e2e8f0"},
    "tech-steel-furnace": {icon: GiFurnace, color: "#b91c1c", background: "#fee2e2"},
    "tech-automation": {icon: GiProcessor, color: "#0f766e", background: "#ecfeff"},
    "tech-transport-belt": {icon: GiBelt, color: "#92400e", background: "#fff7ed"},
    "tech-inserter": {icon: GiRobotGrab, color: "#0ea5e9", background: "#ecfeff"},
}; 

export const getItemIconConfig = (itemId: string) => ITEM_ICON_MAP[itemId];

const IconShell = styled.div<{ $size: number; $background: string; $color: string; $borderColor?: string }>`
  width: ${props => props.$size}px;
  height: ${props => props.$size}px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.$background};
  color: ${props => props.$color};
  border: 1px solid ${props => props.$borderColor ?? "rgba(0,0,0,0.08)"};
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.8);
`;

export function ItemIcon({itemId, size = 32, outlineColor, title, background, color}: { itemId: string, size?: number, outlineColor?: string, title?: string, background?: string, color?: string }) {
    const config = getItemIconConfig(itemId);
    const IconComponent = config?.icon ?? FaBoxOpen;
    const backgroundColor = background ?? config?.background ?? "transparent";
    const iconColor = color ?? config?.color ?? "#0b3d91";
    return <IconShell $size={size} $background={backgroundColor} $color={iconColor} $borderColor={outlineColor} title={title}>
        <IconComponent size={size * 0.65} />
    </IconShell>;
}
